import {
  AddIcon,
  ChevronSmallDownIcon,
  ChevronSmallRightIcon,
  CodeIcon,
  ConnectedIcon,
  DeleteIcon,
  EditIcon,
  FolderDefaultIcon,
  FolderOpenIcon,
  IconButton,
  IconMenuTrigger,
  Menu,
  MenuContent,
  MenuItem,
  MoreIcon,
  Text,
  Tooltip,
  View,
} from '@packages/systems/spring';
import {
  selectComponentCanvasCodeComponentSource,
  selectComponentCanvasComponent,
  selectComponentCanvasComponentId,
} from '@packages/systems/component-canvas/designer';
import {selectSiteCodeComponentSources} from './selectors';
import {
  buildFileImporterIndex,
  describeSourceFileWriteRejection,
  isComponentFilename,
  scopeToComponent,
  validateSourceFileWrite,
  type ComponentSource,
  type FileImporter,
  type SourceFilesState,
} from '@packages/systems/code-libraries/core';
import {sourceFileKey} from '@packages/domains/code-libraries/data-types';
import {mainSourceDraftKey} from './flux/CodeComponentUIState';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {
  useDesignerSelector,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  selectSiteId,
  selectWorkspaceId,
} from '@packages/systems/designer/site-data';
import {ANALYTICS_EVENTS} from '@packages/systems/core/constants/analytics';
import {trackEvent} from '@packages/utilities/analytics';
import {extensionOf} from './sourceFileFormatParser';
// eslint-disable-next-line webflow/module-top-level-imports
import {selectIsAgentTurnInProgress} from '@packages/systems/ai/assistant-next/frontend/selectors';
import {Component} from '@packages/systems/wfdl';
import {emotionStyled, tokens} from '@packages/utilities/styled';
import React from 'react';

import {
  SourceFileConfirmDialog,
  type SourceFileConfirmRequest,
} from './SourceFileConfirmDialog';
import {SourceFileNameInput} from './SourceFileNameInput';
import {BUILDING_LABEL} from './sourceFileLabels';
import {
  vccSourceFileCreated,
  vccSourceFileDeleted,
  vccSourceFileRenamed,
} from './flux/actionCreators';
import {
  selectActiveSourceFileKey,
  selectDirtySourceFileKeys,
  selectSandboxJobStatus,
  selectSourceFiles,
  useCodeComponentDispatch,
  useCodeComponentSelector,
  useCodeComponentSourceFileSelected,
} from './flux/CodeLibrariesSlice';

const BASE_PADDING_PX = 8;
const INDENT_PX = 12;

type FileTreeNode =
  | {
      type: 'folder';
      id: string;
      /** Slash-joined segments from the root, e.g. `utils/format`. */
      path: string;
      label: string;
      depth: number;
      children: FileTreeNode[];
    }
  | {type: 'file'; id: string; label: string; depth: number; fileKey: string};

// Folders first, then files; alphabetical within each group. Recurses.
function sortNodes(nodes: FileTreeNode[]): void {
  nodes.sort((a, b) => {
    if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
    return a.label.localeCompare(b.label);
  });
  for (const node of nodes) {
    if (node.type === 'folder') sortNodes(node.children);
  }
}

/**
 * Builds a nested folder/file tree from the flat SourceFilesStore. Agent-written
 * files carry their folder structure as `/`-separated segments in `name`
 * (folderId is null for sandbox-committed files), so we split on `/` to
 * synthesize virtual folder nodes. Exported for unit testing.
 */
export function buildSourceFileTree(files: SourceFilesState): FileTreeNode[] {
  const roots: FileTreeNode[] = [];
  const folders = new Map<string, Extract<FileTreeNode, {type: 'folder'}>>();

  for (const [fileKey, file] of Object.entries(files)) {
    const segments = file.name
      .split('/')
      .filter((segment) => segment.length > 0);
    // Pop the leaf (the file name); what remains are the folder segments.
    const fileName = segments.pop();
    if (fileName === undefined) continue;

    let siblings = roots;
    let prefix = '';
    segments.forEach((segment, depth) => {
      prefix = prefix ? `${prefix}/${segment}` : segment;
      const folderId = `dir:${prefix}`;
      let folder = folders.get(folderId);
      if (!folder) {
        folder = {
          type: 'folder',
          id: folderId,
          path: prefix,
          label: segment,
          depth,
          children: [],
        };
        folders.set(folderId, folder);
        siblings.push(folder);
      }
      siblings = folder.children;
    });

    siblings.push({
      type: 'file',
      id: fileKey,
      label: fileName,
      depth: segments.length,
      fileKey,
    });
  }

  sortNodes(roots);
  return roots;
}

const TreeContainer = emotionStyled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  minHeight: 0,
});

const TreeHeader = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 4,
  height: 28,
  flex: '0 0 auto',
  paddingLeft: BASE_PADDING_PX,
  paddingRight: 4,
  // Separates the label from the rows below it, matching the rule under the
  // editor's own header.
  borderBottom: `1px solid ${tokens.colors.borderDefault}`,
});

const TreeScroll = emotionStyled('div')({
  width: '100%',
  flex: '1 1 auto',
  minHeight: 0,
  overflowY: 'auto',
  overflowX: 'hidden',
  padding: '4px 0',
  userSelect: 'none',
});

// A div rather than a button: the row hosts its own action menu trigger, and a
// button inside a button is invalid and unreachable by keyboard.
const TreeRow = emotionStyled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  width: '100%',
  minHeight: 24,
  paddingRight: 4,
  cursor: 'pointer',
  textAlign: 'left',
  '&:hover': {backgroundColor: tokens.colors.uiBackgroundHover},
  '&[data-selected="true"]': {
    backgroundColor: tokens.colors.uiBackgroundActive,
  },
  '&[data-selected="true"]:hover': {
    backgroundColor: tokens.colors.uiBackgroundActive,
  },
  // The action trigger stays hidden until the row is hovered or focused. It is
  // pinned while its menu is open, because the popup portals out of this
  // subtree and neither :hover nor :focus-within matches the row any more.
  '& [data-row-actions]': {visibility: 'hidden'},
  '&:hover [data-row-actions]': {visibility: 'visible'},
  '&:focus-within [data-row-actions]': {visibility: 'visible'},
  '&[data-menu-open="true"] [data-row-actions]': {visibility: 'visible'},
});

const RowActions = emotionStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  paddingLeft: 4,
  flex: '0 0 auto',
});

// The filename owns the row's free space (`flex={1}`), so trailing adornments
// sit at the edge without shrinking it.
const SharedMarker = emotionStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 2,
  paddingLeft: 4,
  flex: '0 0 auto',
});

const UNSAVED_LABEL = 'Unsaved changes';

// Goes in the row's leading slot, a blank spacer on files (only folders put a
// chevron there), so the mark costs the filename no width.
const UnsavedMarker = emotionStyled('span')({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: tokens.colors.yellowIcon,
});

const IconSlot = emotionStyled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  flex: '0 0 auto',
});

/**
 * Tree-item interactions include labels and icons, but not action-menu controls.
 *
 * DOM-based, so it only sees what is physically inside the row. `MenuContent`
 * portals its items out of this subtree while still bubbling React events
 * through it, so a keypress on a menu item looks like a row interaction here.
 * `RowActions` stops click and keydown for that reason -- this predicate is not
 * enough on its own.
 */
export function isTreeRowInteraction(
  event: Pick<React.SyntheticEvent, 'target'>
): boolean {
  return !(
    event.target instanceof Element &&
    event.target.closest('[data-row-actions]') !== null
  );
}

/**
 * How many `treeitem`s a level holds. Every row at that level has to report
 * this same number as its `aria-setsize` -- a mismatch across siblings is
 * announced wrong -- so the two extras that are not tree nodes have to be
 * counted here: the component's main entry, appended at the root, and an open
 * create row.
 */
export function getLevelSetSize(
  nodeCount: number,
  hasMainEntry: boolean,
  hasCreateRow: boolean
): number {
  return nodeCount + (hasMainEntry ? 1 : 0) + (hasCreateRow ? 1 : 0);
}

/**
 * The create row is a `treeitem` sibling of the rows at its level, so it needs
 * the same triplet they carry -- a level-N sibling reporting a different
 * `aria-setsize` than its neighbours is announced wrong by screen readers.
 */
export function getCreateRowAriaPosition(
  depth: number,
  setSize: number,
  posInSet: number
): {
  'aria-level': number;
  'aria-setsize': number;
  'aria-posinset': number;
} {
  return {
    'aria-level': depth + 1,
    'aria-setsize': setSize,
    'aria-posinset': posInSet,
  };
}

/** Directory portion of a path-qualified name, with its trailing slash. */
function dirPrefixOf(name: string): string {
  const slashIndex = name.lastIndexOf('/');
  return slashIndex >= 0 ? name.slice(0, slashIndex + 1) : '';
}

/** Leaf portion of a path-qualified name. */
function leafOf(name: string): string {
  const slashIndex = name.lastIndexOf('/');
  return slashIndex >= 0 ? name.slice(slashIndex + 1) : name;
}

const selectUserId = (state: DesignerStores): string =>
  state.SiteDataStore.userId;

type SourceFileEventName =
  | typeof ANALYTICS_EVENTS.VCC_SOURCE_FILE_CREATED
  | typeof ANALYTICS_EVENTS.VCC_SOURCE_FILE_DELETED
  | typeof ANALYTICS_EVENTS.VCC_SOURCE_FILE_RENAMED;

/**
 * Refuses a name for the same reasons the sync would, plus the two only this
 * surface can see: a collision with an existing record, and a component entry
 * file, which the sandbox treats as declaring a whole new component.
 *
 * `replacingKey` is the record being renamed, which is allowed to keep its own
 * key. Exported for unit testing.
 */
export function validateSourceFileName(
  name: string,
  files: SourceFilesState,
  replacingKey?: string
): string | null {
  const rejection = validateSourceFileWrite({name});
  if (rejection) return describeSourceFileWriteRejection(rejection, name);
  if (isComponentFilename(name)) {
    return 'Component files (.webflow.tsx) are created from the canvas, not the file tree.';
  }
  const folderId =
    replacingKey === undefined ? null : (files[replacingKey]?.folderId ?? null);
  const key = sourceFileKey({folderId, name});
  if (key !== replacingKey && files[key] !== undefined) {
    return `"${name}" already exists.`;
  }
  return null;
}

/**
 * Source file tree for the VCC code editor's left rail (EXT-184, EXT-185).
 * Renders the current component's source files (its main source plus the
 * modules it imports) as a folder/file hierarchy; clicking a file dispatches
 * the active-file selection so the editor shows that file's content, and each
 * row carries the create / rename / delete actions.
 * Gated by FEATURE_FLAGS.VCC_MULTIFILE at the call site.
 */
export function SourceFileTree(): React.ReactElement {
  const files = useCodeComponentSelector(selectSourceFiles);
  const activeKey = useCodeComponentSelector(selectActiveSourceFileKey);
  const dirtyKeys = useCodeComponentSelector(selectDirtySourceFileKeys);
  const mainSource = useCodeComponentSelector(
    selectComponentCanvasCodeComponentSource
  );
  const component = useCodeComponentSelector(selectComponentCanvasComponent);
  const componentId = useCodeComponentSelector(
    selectComponentCanvasComponentId
  );
  const selectFile = useCodeComponentSourceFileSelected();
  const dispatch = useCodeComponentDispatch();
  const canEditCodeComponents = useDesignerSelector(
    selectDesignerAbility('canEditCodeComponents')
  );
  const isAgentTurnInProgress = useCodeComponentSelector(
    selectIsAgentTurnInProgress
  );
  // Unlike the editor's isRebuilding note, this doesn't exclude the cold
  // sandbox start-up build: there's no message here to keep from firing
  // prematurely, and a rename/delete racing that first build's read of the
  // filesystem is exactly as unsafe as racing any later one.
  const jobStatus = useCodeComponentSelector(selectSandboxJobStatus);
  // Whether file controls are shown at all. A rebuild in flight is not
  // included here -- see canManageFiles and the teardown effect below for
  // why that reason disables the controls instead of unmounting them.
  const canEditFiles = canEditCodeComponents && !isAgentTurnInProgress;
  // File operations follow the editor's own lock: no permission to edit, an
  // agent turn mid-flight that is itself writing files into this same store,
  // or a rebuild in flight -- renaming or deleting mid-rebuild would race the
  // sandbox write the rebuild is reading from.
  const canManageFiles = canEditFiles && jobStatus === 'IDLE';

  const siteId = useDesignerSelector(selectSiteId);
  const workspaceId = useDesignerSelector(selectWorkspaceId);
  const userId = useDesignerSelector(selectUserId);
  const trackFileEvent = React.useCallback(
    (name: SourceFileEventName, fileName: string) => {
      trackEvent({
        name,
        data: {
          componentId,
          siteId,
          workspaceId: workspaceId ?? undefined,
          userId,
          extension: extensionOf(fileName),
        },
      });
    },
    [componentId, siteId, workspaceId, userId]
  );

  const [collapsed, setCollapsed] = React.useState<ReadonlySet<string>>(
    () => new Set<string>()
  );
  // Directory the pending new file goes in ('' for the sandbox root), or null
  // when no create is in progress.
  const [createDir, setCreateDir] = React.useState<string | null>(null);
  const [renamingKey, setRenamingKey] = React.useState<string | null>(null);
  const [confirmRequest, setConfirmRequest] =
    React.useState<SourceFileConfirmRequest | null>(null);
  // Files this tree touched that reachability alone would hide: a file it just
  // created, which nothing imports yet, and a file it just renamed, whose
  // importers still name the old path. Both need to stay on screen so the user
  // can go fix the imports.
  const [pinnedKeys, setPinnedKeys] = React.useState<ReadonlySet<string>>(
    () => new Set<string>()
  );

  // Those keys belong to the component that was open when they were pinned;
  // another component's tree must not inherit them.
  React.useEffect(() => {
    setPinnedKeys(new Set<string>());
    setCreateDir(null);
    setRenamingKey(null);
    setConfirmRequest(null);
  }, [componentId]);

  // Scope to the current component's files (main source + its transitive
  // relative imports) so files from other components on the site aren't shown.
  // The file being edited stays visible even if nothing imports it, so removing
  // its last import doesn't yank it out from under the editor.
  const alwaysInclude = React.useMemo(() => {
    const keys = new Set(pinnedKeys);
    if (activeKey) keys.add(activeKey);
    return keys;
  }, [pinnedKeys, activeKey]);
  const tree = React.useMemo(
    () =>
      buildSourceFileTree(
        scopeToComponent(mainSource?.code ?? '', files, alwaysInclude)
      ),
    [mainSource, files, alwaysInclude]
  );
  // Which components import each file, so a module shared with another
  // component is visibly shared rather than looking like it belongs here alone.
  const componentSources = useCodeComponentSelector(
    selectSiteCodeComponentSources
  );
  // `selectSiteCodeComponentSources` rebuilds its array whenever the live
  // component map identity changes, which is every staged canvas edit. Keying
  // the walk on that array alone would re-parse every component's imports
  // site-wide while the editor pane is open, for edits that cannot affect the
  // index. The walk only reads each component's id and code, so compare those
  // and reuse the previous index when they are unchanged.
  const indexCacheRef = React.useRef<{
    sources: readonly ComponentSource[];
    files: SourceFilesState;
    index: ReadonlyMap<string, readonly FileImporter[]>;
  } | null>(null);
  const importersByKey = React.useMemo(() => {
    const cached = indexCacheRef.current;
    const isUnchanged =
      cached !== null &&
      cached.files === files &&
      cached.sources.length === componentSources.length &&
      cached.sources.every((source, index) => {
        const next = componentSources[index];
        return (
          next !== undefined &&
          source.id === next.id &&
          source.code === next.code
        );
      });
    if (isUnchanged) return cached.index;
    const index = buildFileImporterIndex(componentSources, files);
    indexCacheRef.current = {sources: componentSources, files, index};
    return index;
  }, [componentSources, files]);
  // Filtered by id: display names are not unique, so comparing them would drop
  // a genuinely different component that happens to share the open one's name.
  const otherImportersOf = React.useCallback(
    (fileKey: string): string[] =>
      (importersByKey.get(fileKey) ?? [])
        .filter((importer) => importer.id !== componentId)
        .map((importer) => importer.name),
    [importersByKey, componentId]
  );

  const toggleFolder = React.useCallback((folderId: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  }, []);

  const validateName = React.useCallback(
    (name: string, replacingKey?: string): string | null =>
      validateSourceFileName(name, files, replacingKey),
    [files]
  );

  const startCreate = React.useCallback(
    (dir: string, folderId?: string) => {
      if (!canManageFiles) return;
      setRenamingKey(null);
      setCreateDir(dir);
      // The input renders among the folder's children, so a collapsed folder
      // would swallow it.
      if (folderId !== undefined) {
        setCollapsed((prev) => {
          if (!prev.has(folderId)) return prev;
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      }
    },
    [canManageFiles]
  );

  // The gate can close while an input is already open -- an agent turn starts,
  // or the user moves to a secondary locale. Tearing the flow down stops a
  // commit from landing at exactly the moment the gate exists to prevent it.
  // Keyed on canEditFiles, not canManageFiles: a rebuild alone shouldn't
  // discard a name the user is mid-typing or an open delete confirmation --
  // commitCreate/commitRename/handleConfirm already refuse to land while
  // canManageFiles is false, so nothing unsafe can happen by leaving it open.
  React.useEffect(() => {
    if (canEditFiles) return;
    setCreateDir(null);
    setRenamingKey(null);
    setConfirmRequest(null);
  }, [canEditFiles]);

  const commitCreate = React.useCallback(
    (name: string) => {
      if (!canManageFiles) return;
      setCreateDir(null);
      // Empty on purpose: the editor opens on the new file for the user to
      // type into, and a placeholder body would just have to be deleted.
      dispatch(vccSourceFileCreated({name, content: ''}));
      trackFileEvent(ANALYTICS_EVENTS.VCC_SOURCE_FILE_CREATED, name);
      const key = sourceFileKey({name});
      setPinnedKeys((prev) => new Set(prev).add(key));
      selectFile(key);
    },
    [canManageFiles, dispatch, selectFile, trackFileEvent]
  );

  const commitRename = React.useCallback(
    (fileKey: string, name: string) => {
      if (!canManageFiles) return;
      const existing = files[fileKey];
      if (existing === undefined) return;
      const nextKey = sourceFileKey({folderId: existing.folderId, name});
      dispatch(vccSourceFileRenamed({key: fileKey, name}));
      trackFileEvent(ANALYTICS_EVENTS.VCC_SOURCE_FILE_RENAMED, name);
      // Pin the new key: every importer still names the old path, so the file
      // is unreachable until the user updates them and would otherwise vanish
      // from the tree the moment it was renamed.
      setPinnedKeys((prev) => {
        const next = new Set(prev);
        next.delete(fileKey);
        next.add(nextKey);
        return next;
      });
      // The key encodes the name, so the selection would otherwise point at a
      // record that no longer exists.
      if (activeKey === fileKey) selectFile(nextKey);
    },
    [canManageFiles, dispatch, files, activeKey, selectFile, trackFileEvent]
  );

  const submitRename = React.useCallback(
    (fileKey: string, name: string) => {
      // Checked before tearing the input down, not just inside commitRename:
      // a rebuild in flight makes both landing the rename and opening the
      // confirm dialog below unsafe, and the user should get their typed name
      // back once it's done rather than have Enter silently discard it.
      if (!canManageFiles) return;
      setRenamingKey(null);
      const existing = files[fileKey];
      if (existing === undefined) return;
      const importers = otherImportersOf(fileKey);
      // Renaming rewrites no import specifiers, so every importer breaks --
      // the current component included. Confirm whenever anything imports the
      // file; a module nothing imports yet has nothing to break.
      if (importersByKey.has(fileKey)) {
        setConfirmRequest({
          action: 'rename',
          fileKey,
          fileName: leafOf(existing.name),
          nextName: name,
          otherImporters: importers,
        });
        return;
      }
      commitRename(fileKey, name);
    },
    [canManageFiles, files, otherImportersOf, importersByKey, commitRename]
  );

  const commitDelete = React.useCallback(
    (fileKey: string) => {
      if (!canManageFiles) return;
      const existing = files[fileKey];
      dispatch(vccSourceFileDeleted({key: fileKey}));
      if (existing !== undefined) {
        trackFileEvent(ANALYTICS_EVENTS.VCC_SOURCE_FILE_DELETED, existing.name);
      }
      setPinnedKeys((prev) => {
        if (!prev.has(fileKey)) return prev;
        const next = new Set(prev);
        next.delete(fileKey);
        return next;
      });
      if (activeKey === fileKey) selectFile(null);
    },
    [canManageFiles, dispatch, files, activeKey, selectFile, trackFileEvent]
  );

  const requestDelete = React.useCallback(
    (fileKey: string) => {
      const existing = files[fileKey];
      if (existing === undefined) return;
      setConfirmRequest({
        action: 'delete',
        fileKey,
        fileName: leafOf(existing.name),
        otherImporters: otherImportersOf(fileKey),
      });
    },
    [files, otherImportersOf]
  );

  const handleConfirm = React.useCallback(() => {
    if (confirmRequest === null || !canManageFiles) return;
    setConfirmRequest(null);
    if (confirmRequest.action === 'delete') {
      commitDelete(confirmRequest.fileKey);
    } else if (confirmRequest.nextName !== undefined) {
      // Re-checked against the files as they are now, not as they were when
      // the dialog opened. The reducer no-ops on a taken key, so committing
      // blind would report success while pinning and selecting someone else's
      // file. Re-opening the input remounts it with the current name and no
      // error, so the refusal is silent -- surfacing the reason needs the
      // attempted name and violation threaded back in (EXT-1926).
      const violation = validateName(
        confirmRequest.nextName,
        confirmRequest.fileKey
      );
      if (violation !== null) {
        setRenamingKey(confirmRequest.fileKey);
        return;
      }
      commitRename(confirmRequest.fileKey, confirmRequest.nextName);
    }
  }, [
    confirmRequest,
    canManageFiles,
    commitDelete,
    commitRename,
    validateName,
  ]);

  const renderCreateInput = (
    dir: string,
    depth: number,
    setSize: number,
    posInSet: number
  ): React.ReactNode => (
    <View
      alignItems="center"
      aria-label="New file"
      display="flex"
      gap="$1"
      key={`__create__:${dir}`}
      paddingRight="$1"
      role="treeitem"
      // Indentation is per-depth, so it can only be computed at render time.
      style={{paddingLeft: BASE_PADDING_PX + depth * INDENT_PX}}
      {...getCreateRowAriaPosition(depth, setSize, posInSet)}
    >
      <IconSlot aria-hidden />
      <IconSlot aria-hidden>
        <CodeIcon />
      </IconSlot>
      <SourceFileNameInput
        ariaLabel="New file name"
        automationId="vcc-source-file-create-input"
        initialValue=""
        onCancel={() => setCreateDir(null)}
        onSubmit={(value) => commitCreate(dir ? `${dir}/${value}` : value)}
        placeholder="helpers.ts"
        validate={(value) =>
          // Checked ahead of validateName, not just inside commitCreate: an
          // Enter that reaches a violation-free name still shouldn't close
          // the input while a rebuild makes committing it unsafe, or the
          // typed name is lost with no explanation. Phrased as a full
          // sentence, not just BUILDING_LABEL -- SourceFileNameInput renders
          // any non-null validate result as a validation failure, and a
          // valid name blocked only by timing isn't one.
          canManageFiles
            ? validateName(dir ? `${dir}/${value}` : value)
            : `${BUILDING_LABEL} You can add this file when that finishes.`
        }
      />
    </View>
  );

  const rows: React.ReactNode[] = [];
  // The DOM is a flat list rather than nested `role="group"` wrappers, so
  // `aria-level`/`aria-setsize`/`aria-posinset` are what let assistive tech
  // reconstruct the hierarchy (siblings are the nodes within the same
  // `nodes` array at each recursion level).
  // The create row is rendered first at whatever level it is open on, so it
  // both grows that level's set and shifts every node after it by one.
  const renderNodes = (nodes: FileTreeNode[], hasCreateRow = false): void => {
    nodes.forEach((node, index) => {
      const indentStyle: React.CSSProperties = {
        paddingLeft: BASE_PADDING_PX + node.depth * INDENT_PX,
      };
      const ariaPosition = {
        'aria-level': node.depth + 1,
        // The main-entry row is always appended as an extra level-1 sibling
        // (see below), so the root nodes' set must count it too, or a screen
        // reader announces mismatched set sizes across level-1 siblings.
        'aria-setsize': getLevelSetSize(
          nodes.length,
          node.depth === 0,
          hasCreateRow
        ),
        'aria-posinset': index + 1 + (hasCreateRow ? 1 : 0),
      } as const;
      if (node.type === 'folder') {
        const isCollapsed = collapsed.has(node.id);
        rows.push(
          <FolderRow
            ariaPosition={ariaPosition}
            canEditFiles={canEditFiles}
            canManageFiles={canManageFiles}
            indentStyle={indentStyle}
            isCollapsed={isCollapsed}
            key={node.id}
            label={node.label}
            onCreateChild={() => startCreate(node.path, node.id)}
            onToggle={() => toggleFolder(node.id)}
          />
        );
        if (!isCollapsed) {
          const hasChildCreateRow = createDir === node.path;
          if (hasChildCreateRow) {
            rows.push(
              renderCreateInput(
                node.path,
                node.depth + 1,
                node.children.length + 1,
                1
              )
            );
          }
          renderNodes(node.children, hasChildCreateRow);
        }
      } else {
        const file = files[node.fileKey];
        rows.push(
          <FileRow
            ariaPosition={ariaPosition}
            canEditFiles={canEditFiles}
            canManageFiles={canManageFiles}
            indentStyle={indentStyle}
            isDirty={dirtyKeys.has(node.fileKey)}
            isRenaming={renamingKey === node.fileKey}
            isSelected={node.fileKey === activeKey}
            key={node.id}
            label={node.label}
            onDelete={() => requestDelete(node.fileKey)}
            onRenameCancel={() => setRenamingKey(null)}
            onRenameStart={() => {
              setCreateDir(null);
              setRenamingKey(node.fileKey);
            }}
            onRenameSubmit={(value) =>
              submitRename(
                node.fileKey,
                `${dirPrefixOf(file?.name ?? node.label)}${value}`
              )
            }
            onSelect={() => selectFile(node.fileKey)}
            otherImporters={otherImportersOf(node.fileKey)}
            validateRename={(value) =>
              validateName(
                `${dirPrefixOf(file?.name ?? node.label)}${value}`,
                node.fileKey
              )
            }
          />
        );
      }
    });
  };

  // Render the imported files/folders first so the main entry sits below them.
  // Level 1 always holds the main entry too, hence the +1 before the create row.
  const hasRootCreateRow = createDir === '';
  const rootSetSize = getLevelSetSize(tree.length, true, hasRootCreateRow);
  if (hasRootCreateRow) rows.push(renderCreateInput('', 0, rootSetSize, 1));
  renderNodes(tree, hasRootCreateRow);

  // The component's main entry (`.webflow.tsx` at the sandbox root, sibling to
  // its imported folder). Selecting it clears the active file, returning the
  // editor to the editable component source — so it's highlighted by default.
  const isMainSelected = activeKey === null;
  const isMainDirty =
    componentId !== undefined && dirtyKeys.has(mainSourceDraftKey(componentId));
  const mainLabel = component
    ? Component.getDisplayName(component)
    : 'Component';
  rows.push(
    <TreeRow
      aria-label={isMainDirty ? `${mainLabel}. ${UNSAVED_LABEL}` : mainLabel}
      aria-level={1}
      aria-posinset={rootSetSize}
      aria-selected={isMainSelected}
      data-selected={isMainSelected}
      aria-setsize={rootSetSize}
      key="__component_main__"
      onClick={() => selectFile(null)}
      onKeyDown={(event) => {
        if (!isTreeRowInteraction(event)) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          selectFile(null);
        }
      }}
      role="treeitem"
      style={{paddingLeft: BASE_PADDING_PX}}
      tabIndex={0}
    >
      <IconSlot aria-hidden>{isMainDirty && <UnsavedMarker />}</IconSlot>
      <IconSlot aria-hidden>
        <CodeIcon />
      </IconSlot>
      <Text
        colorVariant={isMainSelected ? 'primary' : 'secondary'}
        flex={1}
        font="body"
        truncateToLines={1}
      >
        {mainLabel}
      </Text>
    </TreeRow>
  );

  return (
    <TreeContainer>
      <TreeHeader>
        <Text font="helpText" colorVariant="dimmed">
          Files
        </Text>
        {canEditFiles && (
          <IconButton
            Icon={AddIcon}
            data-automation-id="vcc-source-file-create"
            density="compact"
            isDisabled={!canManageFiles}
            label={canManageFiles ? 'New file' : `New file — ${BUILDING_LABEL}`}
            onClick={() => startCreate('')}
            variant="ghost"
            withTooltip
          />
        )}
      </TreeHeader>
      <TreeScroll role="tree" aria-label="Source files">
        {rows}
      </TreeScroll>
      {confirmRequest !== null && (
        <SourceFileConfirmDialog
          isConfirmDisabled={!canManageFiles}
          onCancel={() => setConfirmRequest(null)}
          onConfirm={handleConfirm}
          request={confirmRequest}
        />
      )}
    </TreeContainer>
  );
}

type AriaPosition = {
  'aria-level': number;
  'aria-setsize': number;
  'aria-posinset': number;
};

function FolderRow({
  ariaPosition,
  canEditFiles,
  canManageFiles,
  indentStyle,
  isCollapsed,
  label,
  onCreateChild,
  onToggle,
}: {
  ariaPosition: AriaPosition;
  canEditFiles: boolean;
  canManageFiles: boolean;
  indentStyle: React.CSSProperties;
  isCollapsed: boolean;
  label: string;
  onCreateChild: () => void;
  onToggle: () => void;
}): React.ReactElement {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  return (
    <TreeRow
      aria-expanded={!isCollapsed}
      {...ariaPosition}
      data-menu-open={isMenuOpen}
      onClick={onToggle}
      onKeyDown={(event) => {
        // Enter/Space/F2 bubble up from the action-menu trigger inside this
        // row. Acting on them here would select the file (or toggle the
        // folder) instead of opening the menu, which is exactly the keyboard
        // reachability the button-to-div restructure was meant to preserve.
        if (!isTreeRowInteraction(event)) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      role="treeitem"
      style={indentStyle}
      tabIndex={0}
    >
      <IconSlot aria-hidden>
        {isCollapsed ? <ChevronSmallRightIcon /> : <ChevronSmallDownIcon />}
      </IconSlot>
      <IconSlot aria-hidden>
        {isCollapsed ? <FolderDefaultIcon /> : <FolderOpenIcon />}
      </IconSlot>
      <Text colorVariant="secondary" flex={1} font="body" truncateToLines={1}>
        {label}
      </Text>
      {canEditFiles && (
        <RowActions
          data-row-actions
          onClick={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Menu
            onClose={() => setIsMenuOpen(false)}
            onOpen={() => setIsMenuOpen(true)}
            placement="bottom-end"
          >
            <IconMenuTrigger
              Icon={AddIcon}
              density="compact"
              isDisabled={!canManageFiles}
              label={
                canManageFiles
                  ? `Add to ${label}`
                  : `Add to ${label} — ${BUILDING_LABEL}`
              }
              variant="ghost"
              withTooltip
            />
            <MenuContent>
              <MenuItem onClick={onCreateChild}>New file</MenuItem>
            </MenuContent>
          </Menu>
        </RowActions>
      )}
    </TreeRow>
  );
}

function FileRow({
  ariaPosition,
  canEditFiles,
  canManageFiles,
  indentStyle,
  isDirty,
  isRenaming,
  isSelected,
  label,
  onDelete,
  onRenameCancel,
  onRenameStart,
  onRenameSubmit,
  onSelect,
  otherImporters,
  validateRename,
}: {
  ariaPosition: AriaPosition;
  canEditFiles: boolean;
  canManageFiles: boolean;
  indentStyle: React.CSSProperties;
  isDirty: boolean;
  isRenaming: boolean;
  isSelected: boolean;
  label: string;
  onDelete: () => void;
  onRenameCancel: () => void;
  onRenameStart: () => void;
  onRenameSubmit: (value: string) => void;
  onSelect: () => void;
  otherImporters: readonly string[];
  validateRename: (value: string) => string | null;
}): React.ReactElement {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const sharedLabel = `Also used by ${otherImporters.join(
    ', '
  )}. Changes affect every component that uses it.`;
  // The dot is aria-hidden, so anything it says has to be said here instead.
  const rowLabel = [
    label,
    isDirty ? UNSAVED_LABEL : null,
    otherImporters.length > 0 ? sharedLabel : null,
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <TreeRow
      aria-label={isDirty || otherImporters.length > 0 ? rowLabel : undefined}
      aria-selected={isSelected}
      {...ariaPosition}
      data-menu-open={isMenuOpen}
      data-selected={isSelected}
      onClick={() => {
        if (!isRenaming) onSelect();
      }}
      onDoubleClick={(event) => {
        if (!isTreeRowInteraction(event)) return;
        if (canManageFiles && !isRenaming) onRenameStart();
      }}
      onKeyDown={(event) => {
        // Enter/Space/F2 bubble up from the action-menu trigger inside this
        // row. Acting on them here would select the file (or toggle the
        // folder) instead of opening the menu, which is exactly the keyboard
        // reachability the button-to-div restructure was meant to preserve.
        if (!isTreeRowInteraction(event)) return;
        if (isRenaming) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        } else if (event.key === 'F2' && canManageFiles) {
          event.preventDefault();
          onRenameStart();
        }
      }}
      role="treeitem"
      style={indentStyle}
      tabIndex={0}
    >
      <IconSlot aria-hidden>{isDirty && <UnsavedMarker />}</IconSlot>
      <IconSlot aria-hidden>
        <CodeIcon />
      </IconSlot>
      {isRenaming ? (
        <SourceFileNameInput
          ariaLabel="Rename file"
          automationId="vcc-source-file-rename-input"
          initialValue={label}
          onCancel={onRenameCancel}
          onSubmit={onRenameSubmit}
          // Same reasoning as the create input: checked ahead of
          // validateRename so a violation-free name still surfaces why Enter
          // didn't land, instead of submitRename's silent no-op.
          validate={
            canManageFiles
              ? validateRename
              : () =>
                  `${BUILDING_LABEL} You can rename this file when that finishes.`
          }
        />
      ) : (
        <>
          <Text
            colorVariant={isSelected ? 'primary' : 'secondary'}
            flex={1}
            font="body"
            truncateToLines={1}
          >
            {label}
          </Text>
          {otherImporters.length > 0 && (
            <Tooltip label={sharedLabel} placement="right" asChild>
              {/* aria-hidden: the same information is on the row's own
                  aria-label, because a span inside the row can't take focus
                  and its tooltip would never reach a keyboard user. */}
              <SharedMarker aria-hidden>
                <ConnectedIcon colorVariant="dimmed" />
                <Text font="helpText" colorVariant="dimmed">
                  {otherImporters.length}
                </Text>
              </SharedMarker>
            </Tooltip>
          )}
          {canEditFiles && (
            <RowActions
              data-row-actions
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Menu
                onClose={() => setIsMenuOpen(false)}
                onOpen={() => setIsMenuOpen(true)}
                placement="bottom-end"
              >
                <IconMenuTrigger
                  Icon={MoreIcon}
                  density="compact"
                  isDisabled={!canManageFiles}
                  label={
                    canManageFiles
                      ? `Actions for ${label}`
                      : `Actions for ${label} — ${BUILDING_LABEL}`
                  }
                  variant="ghost"
                  withTooltip
                />
                <MenuContent>
                  <MenuItem onClick={onRenameStart} prefix={EditIcon}>
                    Rename
                  </MenuItem>
                  <MenuItem onClick={onDelete} prefix={DeleteIcon}>
                    Delete
                  </MenuItem>
                </MenuContent>
              </Menu>
            </RowActions>
          )}
        </>
      )}
    </TreeRow>
  );
}
